import { FiLock, FiMinus, FiPlus } from "react-icons/fi";
import React, { useState, useEffect } from "react";
import Layout from "../components/Layout/Layout";
import { useCart } from "../context/cart";
import { useAuth } from "../context/auth";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";

const CartPage = () => {
  const [auth] = useAuth();
  const [cart, setCart] = useCart();
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const getAvailableCoupons = async () => {
    try {
      const { data } = await axios.get("/api/v1/coupon/my-coupons", {
        headers: {
          Authorization: `Bearer ${auth?.token}`,
        },
      });

      if (data.success) {
        setAvailableCoupons(data.coupons);
      }
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    if (auth?.token) {
      getAvailableCoupons();
    }
  }, [auth]);
  const PALETTE = {
    DARK_NAVY: "#1C2E4A",
    ACCENT_BROWN: "#7C4F2B",
    LIGHT_CREAM: "#F8F6F3",
  };
  const increaseQuantity = (id) => {
    const updatedCart = cart.map((item) =>
      item._id === id ? { ...item, quantity: (item.quantity || 1) + 1 } : item,
    );

    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  const decreaseQuantity = (id) => {
    const updatedCart = cart.map((item) =>
      item._id === id
        ? { ...item, quantity: Math.max((item.quantity || 1) - 1, 1) }
        : item,
    );

    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };
  // REMOVE ITEM
  const removeCartItem = (id) => {
    const updated = cart.filter((item) => item._id !== id);
    setCart(updated);
    localStorage.setItem("cart", JSON.stringify(updated));
  };
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      return toast.error("Please enter a coupon code");
    }

    try {
      setCouponLoading(true);

      const { data } = await axios.post(
        "/api/v1/coupon/apply-coupon",
        {
          code: couponCode,
          cartTotal: getTotal(),
        },
        {
          headers: {
            Authorization: `Bearer ${auth?.token}`,
          },
        },
      );

      setDiscount(data);
      toast.success(data.message);
    } catch (err) {
      setDiscount(null);
      toast.error(err.response?.data?.message || "Failed to apply coupon");
    } finally {
      setCouponLoading(false);
    }
  };
  // TOTAL
  const getTotal = () =>
    cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const getTotalQuantity = () =>
    cart.reduce((sum, item) => sum + item.quantity, 0);

  const formatPrice = (amount) =>
    amount.toLocaleString("en-IN", { style: "currency", currency: "INR" });

  // COD PAYMENT
  const handleCODPayment = async () => {
    try {
      console.log(cart);
      setProcessingPayment(true);
      await axios.post("/api/v1/product/cod/order", {
        cart,
        coupon: couponCode,
        discountAmount: discount?.discountAmount || 0,
        finalAmount: discount?.finalPrice || getTotal(),
      });

      setCart([]);
      localStorage.removeItem("cart");

      toast.success("Order placed successfully!");
      // navigate("/dashboard/user/orders");
    } catch (err) {
      const errorMessage = err.response?.data?.message || "COD Order Failed";
      toast.error(errorMessage);
    } finally {
      setProcessingPayment(false);
    }
  };

  // ONLINE PAYMENT
  const handleOnlinePayment = async () => {
    try {
      setProcessingPayment(true);

      const {
        data: { order },
      } = await axios.post("/api/v1/product/razorpay/order", {
        amount: (discount?.finalPrice || getTotal()) * 100,
        cart,
      });

      const options = {
        key: "rzp_live_TMYlAcreWZs327",
        amount: order.amount,
        currency: "INR",
        name: "Russh Berry",
        description: "Order Payment",
        order_id: order.id,
        handler: async function (response) {
          try {
            await axios.post("/api/v1/product/razorpay/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              cart,
            });

            setCart([]);
            localStorage.removeItem("cart");

            toast.success("Payment successful!");
            navigate("/");
          } catch (err) {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: auth?.user?.name,
          email: auth?.user?.email,
        },
        theme: {
          color: PALETTE.DARK_NAVY,
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error("Payment initialization failed");
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCheckout = () => {
    if (!auth?.user?.address) {
      toast.error("Please add a delivery address");
      return navigate("/dashboard/user/profile");
    }

    if (paymentMethod === "cod") handleCODPayment();
    else handleOnlinePayment();
  };

  return (
    <Layout>
      <section
        className="w-full py-12 mt-20"
        // style={{ backgroundColor: PALETTE.LIGHT_CREAM }}
      >
        <div className="max-w-7xl mx-auto px-4">
          {/* HEADER */}
          <div className="text-center mb-12">
            <h1
              className="text-4xl md:text-5xl font-light tracking-widest uppercase mb-3"
              style={{ color: PALETTE.DARK_NAVY }}
            >
              Shopping Cart
            </h1>
            <p className="text-gray-600 text-lg">
              {cart.length
                ? `${getTotalQuantity()} item${
                    getTotalQuantity() > 1 ? "s" : ""
                  } in your cart`
                : "Your cart is empty"}
            </p>
          </div>

          {/* EMPTY CART */}
          {cart.length === 0 ? (
            <div className="text-center py-20">
              <h2 className="text-2xl font-light text-gray-700 mb-4">
                Your cart is empty
              </h2>

              <button
                onClick={() => navigate("/")}
                className="px-8 py-3 text-white rounded-lg hover:scale-105 transition-all"
                style={{ backgroundColor: PALETTE.ACCENT_BROWN }}
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {/* LEFT → CART ITEMS */}
              <div className="lg:col-span-2 space-y-4">
                {cart.map((item) => (
                  <div
                    key={item._id}
                    className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-4"
                  >
                    <div className="flex flex-col md:flex-row gap-4">
                      {/* IMAGE */}
                      <div className="flex-shrink-0">
                        <img
                          src={`/${item.photos[0]}`}
                          alt={item.name}
                          className="w-full md:w-32 h-40 object-cover rounded-xl border border-gray-100"
                        />
                      </div>

                      {/* DETAILS */}
                      <div className="flex-1 flex flex-col justify-between">
                        {/* TOP */}
                        <div>
                          <div className="flex justify-between items-start">
                            <div className="pr-4">
                              <h2
                                className="text-xl font-semibold"
                                style={{ color: PALETTE.DARK_NAVY }}
                              >
                                {item.name}
                              </h2>

                              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                {item.description}
                              </p>

                              <div className="flex flex-wrap gap-2 mt-3">
                                {item.size && (
                                  <span className="px-3 py-1 rounded-full bg-gray-100 text-xs font-medium text-gray-700">
                                    Size: {item.size}
                                  </span>
                                )}

                                {item.color && (
                                  <span className="px-3 py-1 rounded-full bg-gray-100 text-xs font-medium text-gray-700 flex items-center gap-2">
                                    <span
                                      className="w-3 h-3 rounded-full border"
                                      style={{
                                        background:
                                          item.color?.hex ||
                                          item.color?.code ||
                                          "#ccc",
                                      }}
                                    />
                                    {item.color?.name || item.color}
                                  </span>
                                )}
                              </div>
                            </div>

                            <button
                              onClick={() => removeCartItem(item._id)}
                              className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-600 transition"
                            >
                              ✕
                            </button>
                          </div>
                        </div>

                        {/* BOTTOM */}
                        <div className="flex items-end justify-between mt-5">
                          {/* QUANTITY */}
                          <div>
                            <p className="text-xs uppercase tracking-wider text-gray-400 mb-2">
                              Quantity
                            </p>

                            <div className="flex items-center h-11 rounded-xl border border-gray-200 bg-gray-50 overflow-hidden">
                              <button
                                onClick={() => decreaseQuantity(item._id)}
                                className="w-10 h-full flex items-center justify-center hover:bg-white transition"
                              >
                                <FiMinus size={16} />
                              </button>

                              <input
                                type="number"
                                min="1"
                                value={item.quantity || 1}
                                onChange={(e) => {
                                  const qty = Math.max(
                                    1,
                                    Number(e.target.value) || 1,
                                  );

                                  const updatedCart = cart.map((cartItem) =>
                                    cartItem._id === item._id
                                      ? {
                                          ...cartItem,
                                          quantity: qty,
                                        }
                                      : cartItem,
                                  );

                                  setCart(updatedCart);
                                  localStorage.setItem(
                                    "cart",
                                    JSON.stringify(updatedCart),
                                  );
                                }}
                                className="w-14 h-full text-center bg-white border-x border-gray-200 outline-none font-semibold
                                [appearance:textfield]
                                [&::-webkit-inner-spin-button]:appearance-none
                                [&::-webkit-outer-spin-button]:appearance-none"
                              />

                              <button
                                onClick={() => increaseQuantity(item._id)}
                                className="w-10 h-full flex items-center justify-center hover:bg-white transition"
                              >
                                <FiPlus size={16} />
                              </button>
                            </div>
                          </div>

                          {/* PRICE */}
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              {formatPrice(item.price)} × {item.quantity || 1}
                            </p>

                            <h2
                              className="text-2xl font-bold leading-none mt-1"
                              style={{ color: PALETTE.ACCENT_BROWN }}
                            >
                              {formatPrice(item.price * (item.quantity || 1))}
                            </h2>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* RIGHT → ORDER SUMMARY */}
              <div className="bg-white p-6 rounded-lg shadow-md sticky top-6 h-fit">
                <h2
                  className="text-2xl font-semibold mb-6 pb-4 border-b"
                  style={{ color: PALETTE.DARK_NAVY }}
                >
                  Order Summary
                </h2>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span>Items</span>
                    <span>{getTotalQuantity()}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>{formatPrice(getTotal())}</span>
                  </div>

                  {discount && (
                    <>
                      <div className="flex justify-between text-green-600">
                        <span>Discount ({discount.discountPercent}%)</span>
                        <span>-{formatPrice(discount.discountAmount)}</span>
                      </div>

                      <div className="border-t pt-3 flex justify-between font-bold">
                        <span>Total</span>
                        <span>{formatPrice(discount.finalPrice)}</span>
                      </div>
                    </>
                  )}

                  {!discount && (
                    <div className="border-t pt-3 flex justify-between font-bold">
                      <span>Total</span>
                      <span>{formatPrice(getTotal())}</span>
                    </div>
                  )}
                </div>

                {/* ADDRESS */}
                {auth?.user?.address ? (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                    <div className="flex justify-between mb-2">
                      <h3 className="font-semibold">Delivery Address</h3>
                      <button
                        onClick={() => navigate("/dashboard/user/profile")}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Change
                      </button>
                    </div>
                    <p className="text-sm">{auth.user.address}</p>
                  </div>
                ) : (
                  <button
                    onClick={() => navigate("/dashboard/user/profile")}
                    className="w-full py-3 bg-yellow-500 text-white rounded-lg"
                  >
                    Add Address
                  </button>
                )}
                {availableCoupons.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold mb-3">Available Coupons</h3>

                    <div className="space-y-3">
                      {availableCoupons.map((coupon) => (
                        <div
                          key={coupon._id}
                          className="border rounded-xl p-4 hover:border-black transition"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <h4 className="font-bold text-lg">
                                {coupon.code}
                              </h4>

                              <p className="text-sm text-gray-500 mt-1">
                                {coupon.discountPercent}% OFF
                              </p>

                              <p className="text-xs text-gray-500">
                                Min Order ₹{coupon.minAmount}
                              </p>
                            </div>

                            <button
                              onClick={() => {
                                setCouponCode(coupon.code);
                                toast.success(`${coupon.code} selected`);
                              }}
                              className="px-5 py-2 rounded-lg bg-black text-white hover:bg-gray-800 transition"
                            >
                              Use
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* COUPON */}
                <div className="mb-6">
                  <h3 className="font-semibold mb-3">Apply Coupon</h3>

                  {/* Input + Button */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) =>
                        setCouponCode(e.target.value.toUpperCase())
                      }
                      placeholder="Enter coupon code"
                      className="w-full flex-1 border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1C2E4A]"
                    />

                    <button
                      onClick={applyCoupon}
                      disabled={couponLoading}
                      className="w-full sm:w-auto px-5 py-3 rounded-lg text-white transition disabled:opacity-60"
                      style={{ backgroundColor: PALETTE.DARK_NAVY }}
                    >
                      {couponLoading ? "Applying..." : "Apply"}
                    </button>
                  </div>

                  {/* Coupon Applied */}
                  {discount && (
                    <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 overflow-hidden">
                      <div className="flex flex-col gap-3">
                        {/* Coupon */}
                        <div className="flex justify-between items-start gap-3">
                          <span className="text-gray-600 font-medium">
                            Coupon
                          </span>
                          <span className="font-semibold text-right break-all">
                            {couponCode}
                          </span>
                        </div>

                        {/* Discount */}
                        <div className="flex justify-between items-start gap-3">
                          <span className="text-gray-600 font-medium">
                            Discount
                          </span>
                          <span className="text-green-600 font-medium text-right break-words">
                            {discount.discountPercent}% (-
                            {formatPrice(discount.discountAmount)})
                          </span>
                        </div>

                        {/* Total */}
                        <div className="border-t pt-3 flex justify-between items-start gap-3">
                          <span className="font-semibold text-lg">
                            New Total
                          </span>
                          <span
                            className="font-bold text-lg text-right break-words"
                            style={{ color: PALETTE.DARK_NAVY }}
                          >
                            {formatPrice(discount.finalPrice)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* PAYMENT METHOD */}
                {auth?.user?.address && (
                  <div className="mb-6">
                    <h3 className="font-semibold mb-3">Payment Method</h3>

                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer">
                      <input
                        type="radio"
                        value="cod"
                        checked={paymentMethod === "cod"}
                        onChange={() => setPaymentMethod("cod")}
                      />
                      Cash on Delivery
                    </label>

                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer mt-2">
                      <input
                        type="radio"
                        value="online"
                        checked={paymentMethod === "online"}
                        onChange={() => setPaymentMethod("online")}
                      />
                      Pay Online (Razorpay)
                    </label>
                  </div>
                )}

                {/* CHECKOUT BUTTON */}
                <button
                  onClick={handleCheckout}
                  disabled={processingPayment || !auth?.user?.address}
                  className="w-full py-4 text-white text-lg rounded-lg shadow-lg hover:bg-[#5982c5] disabled:opacity-50"
                  style={{ backgroundColor: PALETTE.DARK_NAVY }}
                >
                  {processingPayment ? "Processing..." : "Proceed to Checkout"}
                </button>

                {/* <p className="mt-4 text-center text-gray-500 text-sm flex items-center justify-center gap-2">
                  <span className="flex items-center justify-center">
                    <FiLock
                      className="w-4 h-4"
                      style={{ verticalAlign: "middle" }}
                    />
                  </span>
                  <span className="text-xs sm:text-sm leading-tight">
                    Secure checkout powered by Razorpay
                  </span>
                </p> */}
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default CartPage;
